import React from 'react';
import {
    VStack,
    FormControl,
    FormLabel,
    Input,
    HStack,
    Tag,
    TagLabel,
    TagCloseButton,
    IconButton,
    Textarea,
    useColorModeValue,
    Text,
    Box,
} from '@chakra-ui/react';
import { FiX } from 'react-icons/fi';
import MoodSelector from './MoodSelector';

interface CustomFieldValue {
    name: string;
    value: string;
    field_type?: string;
    category?: string;
    unit?: string;
}

interface JournalEntry {
    content: string;
    entry_date: string;
    mood_label?: string;
    tags?: string[];
    custom_fields?: CustomFieldValue[];
}

interface JournalEntryFormProps {
    entry: JournalEntry;
    onChange: (entry: JournalEntry) => void;
    availableTags?: Array<{ name: string; color: string }>;
}

const JournalEntryForm: React.FC<JournalEntryFormProps> = ({
    entry,
    onChange,
    availableTags = [],
}) => {
    const handleChange = (updates: Partial<JournalEntry>) => {
        onChange({ ...entry, ...updates });
    };

    const handleCustomFieldChange = (index: number, value: string) => {
        if (!entry.custom_fields) return;
        
        const newFields = [...entry.custom_fields];
        newFields[index] = { ...newFields[index], value };
        handleChange({ custom_fields: newFields });
    };

    const handleRemoveCustomField = (index: number) => {
        if (!entry.custom_fields) return;
        
        const newFields = entry.custom_fields.filter((_, i) => i !== index);
        handleChange({ custom_fields: newFields });
    };

    const handleRemoveTag = (tag: string) => {
        const newTags = entry.tags?.filter(t => t !== tag);
        handleChange({ tags: newTags });
    };

    return (
        <VStack spacing={4} width="100%">
            <FormControl>
                <FormLabel>Date</FormLabel>
                <Input
                    type="datetime-local"
                    value={entry.entry_date.slice(0, 16)}
                    onChange={e => handleChange({ entry_date: e.target.value })}
                />
            </FormControl>

            <FormControl>
                <FormLabel>Content</FormLabel>
                <Textarea
                    value={entry.content}
                    onChange={e => handleChange({ content: e.target.value })}
                    minHeight="200px"
                    size="lg"
                    resize="vertical"
                    bg={useColorModeValue('white', 'gray.800')}
                    border="1px solid"
                    borderColor={useColorModeValue('gray.200', 'gray.600')}
                    _focus={{
                        borderColor: 'blue.500',
                        boxShadow: 'outline',
                    }}
                    p={4}
                    fontSize="md"
                    lineHeight="tall"
                />
            </FormControl>

            <FormControl>
                <FormLabel>Mood</FormLabel>
                <MoodSelector
                    value={{ label: entry.mood_label || '' }}
                    onChange={mood => handleChange({ mood_label: mood.label })}
                />
            </FormControl>

            <FormControl>
                <FormLabel>Tags</FormLabel>
                <HStack wrap="wrap">
                    {entry.tags?.map(tag => {
                        const tagData = availableTags.find(t => t.name === tag);
                        return (
                            <Tag 
                                key={tag} 
                                size="md"
                            >
                                <Box
                                    w="2"
                                    h="2"
                                    borderRadius="full"
                                    bg={tagData?.color || 'gray.500'}
                                    mr="2"
                                />
                                <TagLabel>{tag}</TagLabel>
                                <TagCloseButton
                                    onClick={() => handleRemoveTag(tag)}
                                />
                            </Tag>
                        );
                    })}
                </HStack>
            </FormControl>

            <FormControl>
                <FormLabel>Custom Fields</FormLabel>
                {entry.custom_fields?.map((field, idx) => (
                    <HStack key={idx} mb={2}>
                        <Input
                            value={field.name}
                            isReadOnly
                            flex="1"
                        />
                        <Input
                            value={field.value}
                            onChange={e => handleCustomFieldChange(idx, e.target.value)}
                            flex="2"
                        />
                        {field.unit && (
                            <Text color="gray.500" fontSize="sm" minWidth="auto">
                                {field.unit}
                            </Text>
                        )}
                        <IconButton
                            aria-label="Remove field"
                            icon={<FiX />}
                            onClick={() => handleRemoveCustomField(idx)}
                        />
                    </HStack>
                ))}
            </FormControl>
        </VStack>
    );
};

export default JournalEntryForm; 
